import {
  Button,
  Card,
  Command,
  CommandItem,
  CommandList,
  Drawer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuTrigger,
  showConfirmationPrompt,
  showDialog,
  Table,
  TableBody,
  useIsMobile,
} from '@bpinternal/ui-kit-next'
import { DropdownMenuSubTrigger } from '@radix-ui/react-dropdown-menu'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import { Check, ChevronLeft, Ellipsis } from 'lucide-react'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { Boundary } from '~/components'
import { PublishWarningDialog } from '~/components/PublishWarningDialog'
import { Spinner } from '~/elements'
import { useDeleteWebPageSource, useWebPageSource } from '~/features/knowledge-bases/hooks/useKnowledgeSource'
import {
  useCrawlScheduleData,
  useResync,
  useUpdateCrawlSchedule,
  useWebSourceCrawl,
} from '~/features/knowledge-bases/hooks/useWebSourceCrawl'
import { useInfiniteScroll } from '~/hooks'
import { useDeleteFile } from '~/hooks/files/useDeleteFile'
import { getQueryKey } from '~/services'
import { cn } from '~/utils'
import { type CrawlFrequency, type KnowledgeBaseDropdownItem, type KnowledgeSource } from '../../types'
import { KnowledgeSourceStatus } from './KnowledgeSourceCell'
import { KnowledgeSourceDrawer } from './KnowledgeSourceDrawer'
import { KnowledgeSourceMobileCard } from './KnowledgeSourceMobileCard'
import { KnowledgeSourceSelectionProvider } from './KnowledgeSourceSelectionContext'
import { KnowledgeSourceTableHeader, KnowledgeSourceTableHeaderRow } from './KnowledgeSourceTableHeader'
import { KnowledgeSourceTableRow } from './KnowledgeSourceTableRow'

type WebPageSourceTableProps = {
  workspaceId: string
  botId: string
  kbId: string
  dsId: string
  knowledgeBaseName: string
}

export const WebPageSourceTable = ({ workspaceId, botId, kbId, dsId, knowledgeBaseName }: WebPageSourceTableProps) => {
  const navigate = useNavigate()
  const { kbId: routeKbId } = useParams({ strict: false })
  const queryClient = useQueryClient()

  const { data, fetchNextPage, hasNextPage, isFetching } = useWebPageSource({ workspaceId, botId, kbId, dsId })
  const webPageSources = data.pages.flatMap((page) => page.files)

  const crawlTask = useWebSourceCrawl({ workspaceId, botId, kbId, dsId })
  const { mutate: updateCrawlSchedule } = useUpdateCrawlSchedule({ workspaceId, botId, kbId })
  const { frequency, lastResync } = useCrawlScheduleData({ workspaceId, botId, kbId, dsId })
  const [crawlFrequency, setCrawlFrequency] = useState<CrawlFrequency>(frequency ?? ('never' as CrawlFrequency))

  const bottomRef = useInfiniteScroll(fetchNextPage, hasNextPage)
  const isMobile = useIsMobile()

  const { mutate: deleteWebPageSource } = useDeleteWebPageSource({ workspaceId, botId })
  const { mutate: deleteFile } = useDeleteFile({ workspaceId, botId })
  const { mutate: resync } = useResync({ workspaceId, botId })

  const [drawerWebPageSource, setDrawerWebPageSource] = useState<KnowledgeSource | null>(null)

  const items: KnowledgeBaseDropdownItem[] = [
    {
      label: `Resync ${lastResync ? `(Last ${DateTime.fromISO(lastResync).toRelative()})` : ''}`,
      onClick: () => {
        resync({ workspaceId, botId, dsId })
      },
    },
    {
      label: 'Remove this source',
      onClick: () => {
        showConfirmationPrompt('Are you sure you want to remove this web page source from your knowledge base ?', {
          title: 'Remove Web Page Source',
          variant: 'danger',
        }).then(() => {
          deleteWebPageSource(
            { workspaceId, botId, kbId, dsId },
            {
              onSuccess: () => {
                navigate({
                  to: '/workspaces/$workspaceId/bots/$botId/knowledge-bases/$kbId',
                  params: { workspaceId, botId, kbId: routeKbId ?? kbId },
                })
              },
            }
          )
        })
      },
      destructive: true,
    },
  ]

  const ManageDropdownMenu = ({ items }: { items: KnowledgeBaseDropdownItem[] }) => {
    const [dropdownMenuOpen, setDropdownMenuOpen] = useState(false)
    const [reconfigureDropdownMenuOpen, setReconfigureDropdownMenuOpen] = useState(false)
    const options: { value: CrawlFrequency; label: string }[] = [
      { value: 'never', label: 'Recrawl: Never' },
      { value: 'daily', label: 'Recrawl: Daily' },
      { value: 'weekly', label: 'Recrawl: Weekly' },
      { value: 'monthly', label: 'Recrawl: Monthly' },
    ]
    const DropdownItem = ({ action }: { action: KnowledgeBaseDropdownItem }) => {
      return (
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation()
            action.onClick()
            setDropdownMenuOpen(false)
          }}
          className={cn(
            'flex justify-between',
            action.destructive && 'text-danger-600 focus:bg-danger-100 focus:text-danger-700'
          )}
        >
          <span>{action.label}</span>
          {action.icon && (
            <action.icon className={cn(action.destructive && 'stroke-danger-600 focus:stroke-danger-700')} />
          )}
        </DropdownMenuItem>
      )
    }

    return (
      <DropdownMenu open={dropdownMenuOpen} onOpenChange={setDropdownMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="secondary">
            <Ellipsis />
            Manage
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuSub open={reconfigureDropdownMenuOpen} onOpenChange={setReconfigureDropdownMenuOpen}>
            <DropdownMenuSubTrigger asChild>
              <DropdownMenuItem className="w-full">
                <ChevronLeft />
                Reconfigure
              </DropdownMenuItem>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <Command>
                <CommandList>
                  {options.map(({ value, label }) => (
                    <CommandItem
                      key={value}
                      value={value}
                      onSelect={(_value) => {
                        const newFrequency: CrawlFrequency = (_value as CrawlFrequency) ?? 'never'
                        setDropdownMenuOpen(false)
                        setReconfigureDropdownMenuOpen(false)
                        showDialog(
                          ({ close }) => (
                            <PublishWarningDialog
                              botId={botId}
                              close={close}
                              onSubmit={() => {
                                setCrawlFrequency(newFrequency)
                                updateCrawlSchedule({ workspaceId, botId, kbId, dsId, frequency: newFrequency })
                              }}
                            />
                          ),
                          {
                            title: 'Warning',
                          }
                        )
                      }}
                    >
                      {label}
                      {value === crawlFrequency ? <Check className="h-4 w-4" /> : null}
                    </CommandItem>
                  ))}
                </CommandList>
              </Command>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          {items.map((item, index) => (
            <DropdownItem key={index} action={item} />
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <KnowledgeSourceSelectionProvider knowledgeSources={webPageSources} dsIds={[]}>
      <div className="flex flex-col gap-4">
        {/* Desktop Header */}
        <div className="hidden w-full items-center gap-4 md:flex">
          <KnowledgeSourceTableHeader
            workspaceId={workspaceId}
            botId={botId}
            kbId={kbId}
            totalItems={webPageSources.length}
            showOrigin={false}
          >
            <ManageDropdownMenu items={items} />
          </KnowledgeSourceTableHeader>
        </div>

        {/* Mobile Header */}
        <div className="block md:hidden">
          <div className="flex w-full items-center justify-between gap-4">
            <div></div>
            <div className="flex items-center gap-4">
              <ManageDropdownMenu items={items} />
              <p className="text-sm font-medium text-muted-foreground">{webPageSources.length} Items</p>
            </div>
          </div>
        </div>

        {/* Mobile View (Cards) */}
        <div className="block md:hidden">
          <div className="space-y-6">
            {webPageSources.map((knowledgeSource) => (
              <KnowledgeSourceMobileCard
                key={knowledgeSource.id}
                knowledgeSource={knowledgeSource}
                onClick={() => setDrawerWebPageSource(knowledgeSource)}
                showOrigin={false}
                status={<KnowledgeSourceStatus status={knowledgeSource.status} />}
              />
            ))}
          </div>
        </div>

        {/* Desktop View (Table) */}
        <div className="hidden md:block">
          <Card>
            <Table>
              <KnowledgeSourceTableHeaderRow showOrigin={false} />
              <TableBody>
                {webPageSources.map((knowledgeSource, index) => (
                  <KnowledgeSourceTableRow
                    key={knowledgeSource.id}
                    index={index}
                    knowledgeSource={knowledgeSource}
                    onClick={() => setDrawerWebPageSource(knowledgeSource)}
                    showOrigin={false}
                  />
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>

        <div ref={bottomRef} />
        {((isFetching && hasNextPage) || crawlTask) && (
          <div className="flex w-full items-center justify-center py-4">
            <Spinner size="6" />
          </div>
        )}

        {drawerWebPageSource && (
          <Boundary>
            <Drawer
              open={drawerWebPageSource !== null}
              onOpenChange={() => setDrawerWebPageSource(null)}
              direction={isMobile ? 'bottom' : 'right'}
              modal={false}
            >
              <KnowledgeSourceDrawer
                workspaceId={workspaceId}
                botId={botId}
                kbId={kbId}
                knowledgeSourceId={drawerWebPageSource.id}
                knowledgeBaseName={knowledgeBaseName}
                handleDelete={() => {
                  if (drawerWebPageSource) {
                    showConfirmationPrompt('Are you sure you want to delete this knowledge source?', {
                      title: 'Delete Knowledge Source',
                      variant: 'danger',
                    }).then(() => {
                      deleteFile(
                        { id: drawerWebPageSource.id },
                        {
                          onSuccess: async () => {
                            await queryClient.invalidateQueries({
                              queryKey: getQueryKey(
                                'workspaces_/$workspaceId_/bots_/$botId_/knowledgeBases_/$kbId_/sources/$tags/all',
                                {
                                  workspaceId,
                                  botId,
                                  kbId,
                                  tags: { dsId: dsId },
                                }
                              ),
                            })
                            setDrawerWebPageSource(null)
                          },
                        }
                      )
                    })
                  }
                }}
              />
            </Drawer>
          </Boundary>
        )}
      </div>
    </KnowledgeSourceSelectionProvider>
  )
}
